import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FiMapPin, FiActivity, FiBriefcase } from '../utils/icons';
import { Location, Organization } from '../types';
import { getAllLocations, getOrganizations } from '../services/adminService';
import { useSidebar } from '../contexts/SidebarContext';
import { useTheme } from '../contexts/ThemeContext';

// Minimize icon
const MinimizeIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// Maximize icon
const MaximizeIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
  </svg>
);

// Move/Drag icon
const MoveIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="5 9 2 12 5 15" />
    <polyline points="9 5 12 2 15 5" />
    <polyline points="15 19 12 22 9 19" />
    <polyline points="19 9 22 12 19 15" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="12" y1="2" x2="12" y2="22" />
  </svg>
);

// Chevron Up icon
const ChevronUpIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

// Chevron Down icon
const ChevronDownIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Sun icon (for light mode toggle)
const SunIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

// Moon icon (for dark mode toggle)
const MoonIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
`;

const PageHeader = styled.div`
  position: absolute;
  top: 80px;
  left: 24px;
  right: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  font-weight: 400;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
`;

const MapContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.surface};

  .mapboxgl-ctrl-group {
    background: ${({ theme }) => theme.colors.surface} !important;
    border: 1px solid ${({ theme }) => theme.colors.divider} !important;
    border-radius: 4px !important;
    box-shadow: ${({ theme }) =>
      theme.colors.background === '#0d1117'
        ? '0 2px 4px rgba(0, 0, 0, 0.1)'
        : '0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1)'
    } !important;
  }

  .mapboxgl-ctrl-group button {
    width: 30px !important;
    height: 30px !important;
  }

  .mapboxgl-ctrl-group button:hover {
    background-color: ${({ theme }) => theme.colors.background} !important;
  }

  .mapboxgl-ctrl-group button + button {
    border-top: 1px solid ${({ theme }) => theme.colors.divider} !important;
  }

  .mapboxgl-ctrl button .mapboxgl-ctrl-icon {
    filter: ${({ theme }) =>
      theme.colors.background === '#0d1117' ? 'none' : 'invert(0.8)'
    };
  }

  .mapboxgl-popup-content {
    background: ${({ theme }) => theme.colors.surface} !important;
    border-radius: 8px !important;
    box-shadow: ${({ theme }) =>
      theme.colors.background === '#0d1117'
        ? '0 4px 6px rgba(0, 0, 0, 0.1)'
        : '0 4px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)'
    } !important;
    padding: 12px !important;
    border: 1px solid ${({ theme }) => theme.colors.divider};
  }

  .mapboxgl-popup-tip {
    display: none;
  }
`;

// Fixed Position Widget Components
const FloatingWidget = styled.div<{ isMinimized: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: ${({ theme }) =>
    theme.colors.background === '#0d1117'
      ? 'rgba(20, 25, 35, 0.95)'
      : 'rgba(255, 255, 255, 0.95)'
  };
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: ${({ theme }) =>
    theme.colors.background === '#0d1117'
      ? 'none'
      : '1px solid rgba(0, 0, 0, 0.08)'
  };
  border-radius: 16px;
  box-shadow: ${({ theme }) =>
    theme.colors.background === '#0d1117'
      ? '0 8px 32px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      : '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)'
  };
  z-index: 20;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-width: 90vw;
  overflow: hidden;

  &:hover {
    box-shadow: ${({ theme }) =>
      theme.colors.background === '#0d1117'
        ? '0 12px 40px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(255, 255, 255, 0.1)'
        : '0 12px 40px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.08)'
    };
    transform: translateY(-2px);
  }
`;

const WidgetHeader = styled.div`
  padding: 12px 16px;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: transparent;
  border-radius: 16px 16px 0 0;
  user-select: none;
`;

const WidgetTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  font-weight: 600;
  flex: 1;
  letter-spacing: 0.3px;
`;

const WidgetControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ControlButton = styled.button`
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${({ theme }) =>
      theme.colors.background === '#0d1117'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.08)'
    };
    color: ${({ theme }) => theme.colors.text.primary};
    transform: scale(1.05);
  }
`;

const WidgetContent = styled.div<{ isMinimized: boolean }>`
  padding: ${({ isMinimized }) => (isMinimized ? '0' : '0 16px 16px 16px')};
  display: ${({ isMinimized }) => (isMinimized ? 'none' : 'flex')};
  flex-direction: column;
  gap: 12px;
  max-height: ${({ isMinimized }) => (isMinimized ? '0' : '400px')};
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.colors.background === '#0d1117'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.2)'
    };
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) =>
      theme.colors.background === '#0d1117'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(0, 0, 0, 0.3)'
    };
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 500;
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const StatCard = styled.div<{ color?: string }>`
  background: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 100px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ color }) => color || '#7c3aed'};
    transform: translateY(-2px);
  }
`;

const StatIconWrapper = styled.div<{ bgColor?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ bgColor }) => bgColor || '#667eea'}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ bgColor }) => bgColor || '#667eea'};
  font-size: 16px;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${({ theme }) => theme.colors.divider};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.div`
  margin-top: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  font-weight: 500;
`;

export const LocationsMapPage: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const { isCollapsed } = useSidebar();
  const { isDarkMode } = useTheme();

  const [locations, setLocations] = useState<Location[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  const [selectedState, setSelectedState] = useState<string>('all');
  const [selectedZone, setSelectedZone] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(true);

  // Widget state
  const [isMinimized, setIsMinimized] = useState(true);

  // Indian zones with their geographic bounds and center coordinates
  const indianZones = {
    all: {
      name: 'All India',
      center: [78.9629, 20.5937] as [number, number],
      zoom: 4.5
    },
    north: {
      name: 'North India',
      center: [76.5, 29.0] as [number, number],
      zoom: 5.5
    },
    south: {
      name: 'South India',
      center: [78.0, 12.5] as [number, number],
      zoom: 5.5
    },
    east: {
      name: 'East India',
      center: [87.0, 23.5] as [number, number],
      zoom: 5.5
    },
    west: {
      name: 'West India',
      center: [73.0, 20.0] as [number, number],
      zoom: 5.5
    },
    central: {
      name: 'Central India',
      center: [78.5, 22.5] as [number, number],
      zoom: 5.5
    },
  };

  // Indian states with their geographic bounds and center coordinates
  const indianStates = {
    all: {
      name: 'All India',
      center: [78.9629, 20.5937] as [number, number],
      bounds: [[68.1766, 6.7474], [97.4025, 35.5087]] as [[number, number], [number, number]],
      zoom: 4.5
    },
    maharashtra: {
      name: 'Maharashtra',
      center: [75.7139, 19.7515] as [number, number],
      bounds: [[72.6369, 15.6025], [80.9050, 22.0280]] as [[number, number], [number, number]],
      zoom: 6.5
    },
    karnataka: {
      name: 'Karnataka',
      center: [76.9366, 15.3173] as [number, number],
      bounds: [[74.0489, 11.5945], [78.5880, 18.4487]] as [[number, number], [number, number]],
      zoom: 6.5
    },
    tamilnadu: {
      name: 'Tamil Nadu',
      center: [78.6569, 11.1271] as [number, number],
      bounds: [[76.2313, 8.0689], [80.3465, 13.5678]] as [[number, number], [number, number]],
      zoom: 6.5
    },
    delhi: {
      name: 'Delhi',
      center: [77.1025, 28.7041] as [number, number],
      bounds: [[76.8388, 28.4042], [77.3465, 28.8832]] as [[number, number], [number, number]],
      zoom: 10
    },
    kerala: {
      name: 'Kerala',
      center: [76.2711, 10.8505] as [number, number],
      bounds: [[74.8861, 8.2970], [77.4118, 12.7916]] as [[number, number], [number, number]],
      zoom: 7
    },
    gujarat: {
      name: 'Gujarat',
      center: [71.8709, 22.2587] as [number, number],
      bounds: [[68.1625, 20.1248], [74.4680, 24.7080]] as [[number, number], [number, number]],
      zoom: 6.5
    },
    rajasthan: {
      name: 'Rajasthan',
      center: [74.2179, 27.0238] as [number, number],
      bounds: [[69.5004, 23.0390], [78.2630, 30.2000]] as [[number, number], [number, number]],
      zoom: 6
    },
    westbengal: {
      name: 'West Bengal',
      center: [87.8550, 22.9868] as [number, number],
      bounds: [[85.8238, 21.4955], [89.8531, 27.2315]] as [[number, number], [number, number]],
      zoom: 7
    },
  };

  const toggleMinimize = () => {
    setIsMinimized((prev) => !prev);
  };

  const handleStateChange = (stateKey: string) => {
    setSelectedState(stateKey);
    setSelectedZone('all'); // Reset zone when state is changed
    if (mapRef.current) {
      const state = indianStates[stateKey as keyof typeof indianStates];
      mapRef.current.flyTo({
        center: state.center,
        zoom: state.zoom,
        bearing: 0,
        pitch: 0,
        duration: 2000,
        essential: true,
        easing: (t) => t * (2 - t),
      });
    }
  };

  const handleZoneChange = (zoneKey: string) => {
    setSelectedZone(zoneKey);
    setSelectedState('all'); // Reset state when zone is changed
    if (mapRef.current) {
      const zone = indianZones[zoneKey as keyof typeof indianZones];
      mapRef.current.flyTo({
        center: zone.center,
        zoom: zone.zoom,
        bearing: 0,
        pitch: 0,
        duration: 2000,
        essential: true,
        easing: (t) => t * (2 - t),
      });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      initializeMap();
    }
  }, []);

  useEffect(() => {
    if (mapRef.current && locations.length > 0) {
      updateMapMarkers();
    }
  }, [locations, selectedOrg]);

  // Resize map when sidebar state changes
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current?.resize();
      }, 300); // Wait for sidebar animation to complete
    }
  }, [isCollapsed]);

  // Update map style when theme changes
  useEffect(() => {
    if (mapRef.current) {
      const newStyle = isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11';
      mapRef.current.setStyle(newStyle);
    }
  }, [isDarkMode]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [locationsResult, orgsResult] = await Promise.all([
        getAllLocations({}, { page: 1, limit: 1000 }),
        getOrganizations({}, { page: 1, limit: 100 }),
      ]);

      if (locationsResult.success) {
        setLocations(locationsResult.data);
      }

      if (orgsResult.success) {
        setOrganizations(orgsResult.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeMap = () => {
    if (!mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
      center: [78.9629, 20.5937], // Center of India
      zoom: 3, // Starting zoom level
      pitch: 0,
      antialias: true, // Enable smooth edges
    });

    // Add styling after map loads
    map.on('style.load', () => {
      setMapLoading(false);

      // Smoothly zoom to India after map loads
      setTimeout(() => {
        map.flyTo({
          center: [78.9629, 20.5937],
          zoom: 4.5,
          bearing: 0,
          duration: 2000,
          essential: true,
          easing: (t) => t * (2 - t), // Smooth easing
        });
      }, 800);

      updateMapMarkers();
    });

    // Add navigation control with custom styling
    map.addControl(new mapboxgl.NavigationControl({
      visualizePitch: true,
    }), 'top-right');

    // Add fullscreen control
    map.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    // Add scale control
    map.addControl(
      new mapboxgl.ScaleControl({
        maxWidth: 100,
        unit: 'metric',
      }),
      'bottom-left'
    );

    mapRef.current = map;
  };

  const updateMapMarkers = () => {
    if (!mapRef.current) return;

    // Remove existing markers
    const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
    existingMarkers.forEach((marker) => marker.remove());

    // Filter locations
    const filteredLocations =
      selectedOrg === 'all'
        ? locations
        : locations.filter((loc) => loc.orgId === parseInt(selectedOrg));

    // Add markers with enhanced styling
    filteredLocations.forEach((location) => {
      if (location.latitude && location.longitude) {
        const titleColor = isDarkMode ? '#e6edf3' : '#1a1d29';
        const textColor = isDarkMode ? '#8b949e' : '#5f6c7b';
        const borderColor = isDarkMode ? '#30363d' : '#e1e4e8';

        const popup = new mapboxgl.Popup({
          offset: 30,
          closeButton: false,
          className: 'custom-popup'
        }).setHTML(`
          <div style="padding: 4px;">
            <h3 style="margin: 0 0 8px 0; font-size: 15px; font-weight: 600; color: ${titleColor};">${location.name}</h3>
            <p style="margin: 0; font-size: 13px; color: ${textColor}; line-height: 1.5;">${location.address}</p>
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid ${borderColor};">
              <span style="display: inline-block; padding: 4px 10px; background: #10b981; color: white; border-radius: 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">
                ${location.status || 'Active'}
              </span>
            </div>
          </div>
        `);

        // Create custom marker element
        const el = document.createElement('div');
        el.className = 'custom-marker';
        el.style.width = '24px';
        el.style.height = '24px';
        el.style.borderRadius = '50%';
        el.style.background = '#06b6d4';
        el.style.border = '2px solid white';
        el.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
        el.style.cursor = 'pointer';
        el.style.transition = 'all 0.2s';

        el.addEventListener('mouseenter', () => {
          el.style.transform = 'scale(1.3)';
          el.style.boxShadow = '0 4px 8px rgba(6, 182, 212, 0.5)';
        });

        el.addEventListener('mouseleave', () => {
          el.style.transform = 'scale(1)';
          el.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
        });

        new mapboxgl.Marker({ element: el })
          .setLngLat([location.longitude, location.latitude])
          .setPopup(popup)
          .addTo(mapRef.current!);
      }
    });

    // Fit bounds to show all markers if we're zoomed in
    if (filteredLocations.length > 0 && mapRef.current.getZoom() > 2) {
      const bounds = new mapboxgl.LngLatBounds();
      filteredLocations.forEach((loc) => {
        if (loc.latitude && loc.longitude) {
          bounds.extend([loc.longitude, loc.latitude]);
        }
      });
      mapRef.current?.fitBounds(bounds, {
        padding: 80,
        pitch: 30,
        duration: 2000
      });
    }
  };

  const filteredLocations =
    selectedOrg === 'all'
      ? locations
      : locations.filter((loc) => loc.orgId === parseInt(selectedOrg));

  const statCards = [
    {
      label: 'Total Locations',
      value: filteredLocations.length,
      icon: FiMapPin,
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    },
    {
      label: 'Active Locations',
      value: filteredLocations.filter((l) => l.status === 'active').length,
      icon: FiActivity,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    },
    {
      label: 'Organizations',
      value: new Set(filteredLocations.map((l) => l.orgId)).size,
      icon: FiBriefcase,
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    },
  ];

  return (
    <PageContainer>
      {/* Fullscreen Map */}
      <MapContainer ref={mapContainerRef}>
        {mapLoading && (
          <LoadingOverlay>
            <LoadingSpinner />
            <LoadingText>Loading map...</LoadingText>
          </LoadingOverlay>
        )}
      </MapContainer>

      {/* Compact Fixed Widget */}
      <FloatingWidget isMinimized={isMinimized}>
        <WidgetHeader>
          <WidgetTitle>Statistics</WidgetTitle>
          <WidgetControls>
            <ControlButton onClick={toggleMinimize} title={isMinimized ? 'Expand' : 'Collapse'}>
              {isMinimized ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </ControlButton>
          </WidgetControls>
        </WidgetHeader>

        <WidgetContent isMinimized={isMinimized}>
          {/* Zone Filter */}
          <FilterRow>
            <FilterLabel>Zone:</FilterLabel>
            <FilterSelect value={selectedZone} onChange={(e) => handleZoneChange(e.target.value)}>
              {Object.entries(indianZones).map(([key, zone]) => (
                <option key={key} value={key}>
                  {zone.name}
                </option>
              ))}
            </FilterSelect>
          </FilterRow>

          {/* State Filter */}
          <FilterRow>
            <FilterLabel>State:</FilterLabel>
            <FilterSelect value={selectedState} onChange={(e) => handleStateChange(e.target.value)}>
              {Object.entries(indianStates).map(([key, state]) => (
                <option key={key} value={key}>
                  {state.name}
                </option>
              ))}
            </FilterSelect>
          </FilterRow>

          {/* Organization Filter */}
          <FilterRow>
            <FilterLabel>Org:</FilterLabel>
            <FilterSelect value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)}>
              <option value="all">All Organizations</option>
              {organizations.map((org) => (
                <option key={org._id} value={org.orgId}>
                  {org.name}
                </option>
              ))}
            </FilterSelect>
          </FilterRow>

          {/* Stats Cards */}
          <StatsGrid>
            {statCards.map((card, index) => (
              <StatCard key={index} color={card.color}>
                <StatIconWrapper bgColor={card.color}>
                  <card.icon />
                </StatIconWrapper>
                <StatValue>{card.value.toLocaleString()}</StatValue>
                <StatLabel>{card.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
        </WidgetContent>
      </FloatingWidget>
    </PageContainer>
  );
};
